import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Lock, UtensilsCrossed, ShoppingBag, CreditCard, Banknote, CheckCircle,
  X, Plus, Minus, Bell, Loader2, ArrowLeft, Clock
} from 'lucide-react';
import axios from 'axios';
import { ServerCheckoutOverlay } from './server/ServerCheckoutOverlay';
import { ServerDashboard } from './server/ServerDashboard';
import { ServerNewOrder } from './server/ServerNewOrder';
import { ServerTableRecap } from './server/ServerTableRecap';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { printKitchenTicket, printClientReceipt } from '../components/PrintTicket';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServerPage = () => {
  const [pin, setPin] = useState('');
  const [server, setServer] = useState(null);
  const [view, setView] = useState('dashboard');
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [isTakeaway, setIsTakeaway] = useState(false);
  const [orderNotes, setOrderNotes] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [checkoutOrder, setCheckoutOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [cashAmount, setCashAmount] = useState('');
  const [cashTip, setCashTip] = useState('');
  const [cbTip, setCbTip] = useState('');
  const [cbWaiting, setCbWaiting] = useState(false);
  const [recapTableId, setRecapTableId] = useState(null);
  const [waiterCalls, setWaiterCalls] = useState([]);
  const [isGrabOrder, setIsGrabOrder] = useState(false);
  const [grabOrderId, setGrabOrderId] = useState('');
  const [orderSource, setOrderSource] = useState('dine_in');
  const [deliveryPlatforms, setDeliveryPlatforms] = useState([]);
  const [platformOrderId, setPlatformOrderId] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('');

  const PLATFORM_META = {
    grab: { name: 'Grab', color: '#00B14F' },
    foodpanda: { name: 'Foodpanda', color: '#D70F64' },
    line_man: { name: 'LINE MAN', color: '#00C300' },
    uber_eats: { name: 'Uber Eats', color: '#000000' },
    deliveroo: { name: 'Deliveroo', color: '#00CCBC' },
    just_eat: { name: 'Just Eat', color: '#FF8000' },
    glovo: { name: 'Glovo', color: '#FFC244' },
    wolt: { name: 'Wolt', color: '#009DE0' },
    bolt_food: { name: 'Bolt Food', color: '#34D186' },
    doordash: { name: 'DoorDash', color: '#FF3008' },
    grubhub: { name: 'Grubhub', color: '#F63440' },
    talabat: { name: 'Talabat', color: '#FF5800' },
    careem: { name: 'Careem', color: '#49B44D' },
    noon_food: { name: 'Noon', color: '#FEEE00' },
    rappi: { name: 'Rappi', color: '#FF441F' },
    ifood: { name: 'iFood', color: '#EA1D2C' },
    custom_delivery: { name: 'Livraison', color: '#8B5CF6' },
  };

  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: pushSubscribe } = usePushNotifications('server', '', server?.id || '');

  const eventSourceRef = useRef(null);

  const formatPrice = (p) => `฿${p?.toLocaleString() || 0}`;

  const fetchData = useCallback(async () => {
    if (!server) return;
    try {
      const [ordersRes, menuRes, catRes] = await Promise.all([
        axios.get(`${API}/server/${server.id}/orders`),
        axios.get(`${API}/menu`),
        axios.get(`${API}/categories`)
      ]);
      setOrders(ordersRes.data.filter(o => !o.is_fully_paid));
      setMenuItems(menuRes.data.filter(i => i.is_active !== false));
      setCategories(catRes.data);
    } catch (e) { console.error(e); }
  }, [server]);

  // Fetch delivery platforms on login
  useEffect(() => {
    if (!server) return;
    const fetchPlatforms = async () => {
      try {
        const res = await axios.get(`${API}/server/${server.id}/platforms`);
        setDeliveryPlatforms(res.data || []);
      } catch { /* ignore - default to empty */ }
    };
    fetchPlatforms();
  }, [server]);

  useEffect(() => { if (server) fetchData(); }, [server, fetchData]);
  useEffect(() => {
    if (!server) return;
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [server, fetchData]);

  // SSE for waiter calls
  useEffect(() => {
    if (!server) return;
    const es = new EventSource(`${API}/events/server`);
    eventSourceRef.current = es;
    es.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'waiter_call') {
          setWaiterCalls(prev => [...prev, { id: data.call_id || Date.now(), table_id: data.table_id, message: data.message, created_at: data.timestamp }]);
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator(); const gain = ctx.createGain();
            osc.connect(gain); gain.connect(ctx.destination);
            osc.frequency.value = 1200; gain.gain.value = 0.3;
            osc.start(); osc.stop(ctx.currentTime + 0.15);
            setTimeout(() => { const o2 = ctx.createOscillator(); const g2 = ctx.createGain(); o2.connect(g2); g2.connect(ctx.destination); o2.frequency.value = 1500; g2.gain.value = 0.3; o2.start(); o2.stop(ctx.currentTime + 0.15); }, 200);
          } catch(e) {}
        }
        if (data.type === 'new_order' || data.type === 'status_update') fetchData();
        if (data.type === 'status_update' && data.status === 'ready') {
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator(); const gain = ctx.createGain();
            osc.connect(gain); gain.connect(ctx.destination);
            osc.frequency.value = 880; osc.type = 'sine'; gain.gain.value = 0.3;
            osc.start(); gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5); osc.stop(ctx.currentTime + 0.5);
            setTimeout(() => { const o2 = ctx.createOscillator(); const g2 = ctx.createGain(); o2.connect(g2); g2.connect(ctx.destination); o2.frequency.value = 1100; g2.gain.value = 0.3; o2.start(); g2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5); o2.stop(ctx.currentTime + 0.5); }, 200);
          } catch {}
        }
      } catch(e) {}
    };
    es.onerror = () => { es.close(); setTimeout(() => {
      if (server) {
        const newEs = new EventSource(`${API}/events/server`);
        eventSourceRef.current = newEs;
      }
    }, 3000); };
    return () => es.close();
  }, [server, fetchData]);

  const handleLogin = async () => {
    try {
      const res = await axios.post(`${API}/server/login`, { pin });
      setServer(res.data.server || res.data);
    } catch { alert('PIN incorrect'); }
  };

  const cartTotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const myTables = server?.assigned_tables || [];

  const filtered = menuItems.filter(item => {
    const matchSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCat = selectedCategory === 'all' || item.category_id === selectedCategory;
    return matchSearch && matchCat;
  }).sort((a, b) => {
    if (selectedCategory !== 'all' || searchQuery) return 0;
    const aFav = a.is_favorite ? 0 : 1;
    const bFav = b.is_favorite ? 0 : 1;
    return aFav - bFav;
  });

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(i => i.item_id === item.id);
      if (existing) return prev.map(i => i.item_id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { item_id: item.id, name: item.name, price: item.price, quantity: 1 }];
    });
  };

  const updateQty = (itemId, delta) => {
    setCart(prev => prev.map(i => i.item_id === itemId ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i).filter(i => i.quantity > 0));
  };

  const startNewOrder = (tableId) => {
    setSelectedTable(tableId || '');
    setIsTakeaway(!tableId);
    setIsGrabOrder(false);
    setGrabOrderId('');
    setCart([]);
    setOrderNotes('');
    setSearchQuery('');
    setSelectedCategory('all');
    setView('neworder');
  };

  const startGrabOrder = () => {
    startPlatformOrder('grab');
  };

  const startPlatformOrder = (platformId) => {
    setSelectedTable('');
    setIsTakeaway(true);
    setIsGrabOrder(true);
    setSelectedPlatform(platformId);
    setGrabOrderId('');
    setPlatformOrderId('');
    setCart([]);
    setOrderNotes('');
    setSearchQuery('');
    setSelectedCategory('all');
    setView('neworder');
  };

  const placeOrder = async () => {
    if (cart.length === 0) return;
    try {
      const payload = {
        table_id: isTakeaway ? `takeaway-${Date.now()}` : selectedTable,
        items: cart.map(i => ({ item_id: i.item_id, name: i.name, price: i.price, quantity: i.quantity })),
        notes: orderNotes,
        server_id: server.id,
        server_name: server.name,
        is_takeaway: isTakeaway,
        order_source: isGrabOrder ? selectedPlatform : orderSource,
        grab_order_id: isGrabOrder ? (platformOrderId || grabOrderId) : '',
      };
      const res = await axios.post(`${API}/server/${server.id}/orders`, payload);
      setCart([]);
      setOrderNotes('');
      setIsGrabOrder(false);
      setGrabOrderId('');
      setCheckoutOrder(res.data);
      setPaymentMethod(null);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const openPaymentForOrder = (order, method) => {
    setCheckoutOrder(order);
    setPaymentMethod(null);
    setCashAmount('');
    setCashTip('');
    setCbTip('');
  };

  const recordCash = async () => {
    if (!checkoutOrder) return;
    try {
      await axios.post(`${API}/admin/orders/${checkoutOrder.id}/cash-payment`, {
        amount: parseFloat(cashAmount), tip_amount: parseFloat(cashTip) || 0
      });
      // Print receipts
      printClientReceipt({
        tableId: checkoutOrder.table_id, items: checkoutOrder.items, total: checkoutOrder.total,
        discountAmount: checkoutOrder.discount_amount, paymentMethod: 'cash',
        tip: parseFloat(cashTip) || 0, orderId: checkoutOrder.id,
        createdAt: checkoutOrder.created_at, serverName: server?.name, isTakeaway: checkoutOrder.is_takeaway,
      });
      setCheckoutOrder(null); setPaymentMethod(null); setCashAmount(''); setCashTip('');
      fetchData(); setView('dashboard');
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const recordCard = async () => {
    if (!checkoutOrder) return;
    try {
      await axios.post(`${API}/admin/orders/${checkoutOrder.id}/payment`, {
        payment_method: 'card_terminal', tip_amount: parseFloat(cbTip) || 0
      });
      printClientReceipt({
        tableId: checkoutOrder.table_id, items: checkoutOrder.items, total: checkoutOrder.total,
        discountAmount: checkoutOrder.discount_amount, paymentMethod: 'card_terminal',
        tip: parseFloat(cbTip) || 0, orderId: checkoutOrder.id,
        createdAt: checkoutOrder.created_at, serverName: server?.name, isTakeaway: checkoutOrder.is_takeaway,
      });
      setCheckoutOrder(null); setPaymentMethod(null); setCbWaiting(false); setCbTip('');
      fetchData(); setView('dashboard');
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const recordOtherPayment = async (method) => {
    if (!checkoutOrder) return;
    try {
      await axios.post(`${API}/admin/orders/${checkoutOrder.id}/payment`, { payment_method: method });
      printClientReceipt({
        tableId: checkoutOrder.table_id, items: checkoutOrder.items, total: checkoutOrder.total,
        discountAmount: checkoutOrder.discount_amount, paymentMethod: method,
        tip: 0, orderId: checkoutOrder.id,
        createdAt: checkoutOrder.created_at, serverName: server?.name, isTakeaway: checkoutOrder.is_takeaway,
      });
      setCheckoutOrder(null); setPaymentMethod(null);
      fetchData(); setView('dashboard');
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const sendWithoutPayment = () => {
    // Print kitchen AND bar tickets when sending to kitchen
    if (checkoutOrder) {
      const kitchenItems = checkoutOrder.items.filter(i => {
        const cat = categories.find(c => c.id === i.category_id);
        return !cat || cat.section !== 'bar';
      });
      const barItems = checkoutOrder.items.filter(i => {
        const cat = categories.find(c => c.id === i.category_id);
        return cat && cat.section === 'bar';
      });
      if (kitchenItems.length > 0) {
        printKitchenTicket({
          tableId: checkoutOrder.table_id, items: kitchenItems,
          notes: checkoutOrder.notes, orderId: checkoutOrder.id,
          createdAt: checkoutOrder.created_at, serverName: server?.name,
          section: 'CUISINE',
        });
      }
      if (barItems.length > 0) {
        printKitchenTicket({
          tableId: checkoutOrder.table_id, items: barItems,
          notes: checkoutOrder.notes, orderId: checkoutOrder.id,
          createdAt: checkoutOrder.created_at, serverName: server?.name,
          section: 'BAR',
        });
      }
      // If no category split possible, send all to kitchen
      if (kitchenItems.length === 0 && barItems.length === 0) {
        printKitchenTicket({
          tableId: checkoutOrder.table_id, items: checkoutOrder.items,
          notes: checkoutOrder.notes, orderId: checkoutOrder.id,
          createdAt: checkoutOrder.created_at, serverName: server?.name,
        });
      }
    }
    setCheckoutOrder(null); setPaymentMethod(null);
    fetchData(); setView('dashboard');
  };

  const dismissWaiterCall = (callId) => {
    setWaiterCalls(prev => prev.filter(c => c.id !== callId));
  };

  const statusColors = {
    received: 'bg-[#E9C46A] text-[#2D241D]',
    preparing: 'bg-[#E16833] text-white',
    ready: 'bg-[#2D6A4F] text-white',
    served: 'bg-[#0A422D] text-white',
  };

  // ─── Login screen ───
  if (!server) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-4" data-testid="server-login-screen">
        <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-lg text-center">
          <div className="w-16 h-16 bg-[#0A422D] rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#2D241D] mb-1">Acces Serveur</h1>
          <p className="text-[#6E5E52] text-sm mb-6">Entrez votre code PIN</p>
          <Input type="password" value={pin} onChange={(e) => setPin(e.target.value)}
            placeholder="PIN" className="h-14 text-center text-2xl tracking-widest rounded-xl border-[#E5DCD3] mb-4 text-[#2D241D] bg-white"
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()} data-testid="server-pin-input" />
          <Button onClick={handleLogin} className="w-full h-12 bg-[#0A422D] hover:bg-[#083323] text-white rounded-full font-semibold" data-testid="server-login-btn">
            Connexion
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col" data-testid="server-page">
      {/* Header */}
      <header className="bg-[#0A422D] text-white px-3 py-2.5 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          {view === 'neworder' ? (
            <Button size="sm" onClick={() => setView('dashboard')}
              className="bg-white/10 text-white hover:bg-white/20 rounded-lg h-8 px-3 text-xs" data-testid="back-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
          ) : view === 'tablerecap' ? (
            <Button size="sm" onClick={() => { setView('dashboard'); setRecapTableId(null); }}
              className="bg-white/10 text-white hover:bg-white/20 rounded-lg h-8 px-3 text-xs" data-testid="back-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
          ) : (
            <span className="text-sm font-semibold">{server.name}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {pushSupported && !pushSubscribed && server && (
            <Button size="sm" onClick={pushSubscribe} variant="ghost" className="text-white/60 hover:text-white hover:bg-white/10 h-8 px-2" data-testid="push-subscribe-server">
              <Bell className="w-4 h-4" />
            </Button>
          )}
          {pushSubscribed && (
            <Bell className="w-3.5 h-3.5 text-[#D4A853] mr-1" />
          )}
          {view === 'dashboard' && (
            <Button size="sm" onClick={() => startNewOrder()}
              className="bg-[#E16833] hover:bg-[#C95A2A] text-white rounded-lg h-8 px-3 text-xs font-semibold" data-testid="new-order-btn">
              <Plus className="w-4 h-4 mr-1" /> Nouvelle commande
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => { setServer(null); setPin(''); }}
            className="text-white/60 hover:text-white hover:bg-white/10 h-8 px-2" data-testid="server-logout-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Waiter Calls Banner */}
      {waiterCalls.length > 0 && (
        <div className="bg-red-500 text-white px-3 py-2 space-y-1" data-testid="waiter-calls-banner">
          {waiterCalls.map(call => (
            <div key={call.id} className="flex items-center justify-between">
              <span className="text-sm flex items-center gap-2">
                <Bell className="w-4 h-4 animate-bounce" />
                <strong>Table {call.table_id}</strong> - {call.message}
              </span>
              <button onClick={() => dismissWaiterCall(call.id)} className="text-xs bg-white/20 rounded-lg px-2 py-1 hover:bg-white/30"
                data-testid={`dismiss-call-${call.id}`}>
                OK
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ═══════ DASHBOARD VIEW ═══════ */}
      {view === 'dashboard' && (
        <ServerDashboard
          orders={orders} myTables={myTables}
          setRecapTableId={setRecapTableId} setView={setView}
          startNewOrder={startNewOrder} startGrabOrder={startGrabOrder}
          startPlatformOrder={startPlatformOrder}
          deliveryPlatforms={deliveryPlatforms}
          PLATFORM_META={PLATFORM_META}
          openPaymentForOrder={openPaymentForOrder}
          formatPrice={formatPrice} statusColors={statusColors}
          serverName={server?.name}
        />
      )}

      {/* ═══════ TABLE RECAP VIEW ═══════ */}
      {view === 'tablerecap' && recapTableId && (
        <ServerTableRecap
          recapTableId={recapTableId} orders={orders} server={server}
          startNewOrder={startNewOrder} openPaymentForOrder={openPaymentForOrder}
          fetchData={fetchData} formatPrice={formatPrice}
        />
      )}

      {/* ═══════ NEW ORDER VIEW (POS) ═══════ */}
      {view === 'neworder' && (
        <ServerNewOrder
          categories={categories} filtered={filtered}
          cart={cart} selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          searchQuery={searchQuery} setSearchQuery={setSearchQuery}
          addToCart={addToCart} updateQty={updateQty}
          selectedTable={selectedTable} setSelectedTable={setSelectedTable}
          isTakeaway={isTakeaway} setIsTakeaway={setIsTakeaway}
          orderNotes={orderNotes} setOrderNotes={setOrderNotes}
          myTables={myTables} cartTotal={cartTotal} placeOrder={placeOrder}
          formatPrice={formatPrice}
          isGrabOrder={isGrabOrder} grabOrderId={grabOrderId} setGrabOrderId={setGrabOrderId}
          orderSource={orderSource} setOrderSource={setOrderSource}
          selectedPlatform={selectedPlatform} platformOrderId={platformOrderId}
          setPlatformOrderId={setPlatformOrderId} PLATFORM_META={PLATFORM_META}
        />
      )}

      {/* CHECKOUT OVERLAY */}
      {checkoutOrder && (
        <ServerCheckoutOverlay
          order={checkoutOrder}
          onClose={sendWithoutPayment}
          onCashPayment={(amount, tip) => {
            setCashAmount(String(amount));
            setCashTip(String(tip));
            setTimeout(recordCash, 100);
          }}
          onCardPayment={() => recordCard()}
          onSplitPayment={(pct) => {
            const remaining = (checkoutOrder.total - (checkoutOrder.discount_amount || 0)) - (checkoutOrder.paid_amount || 0);
            const partialAmount = remaining * pct / 100;
            setCashAmount(String(partialAmount));
            setCashTip('0');
            setTimeout(recordCash, 100);
          }}
          onPromptPayPayment={() => recordOtherPayment('promptpay')}
          onCounterPayment={() => recordOtherPayment('counter')}
          formatPrice={formatPrice}
        />
      )}
    </div>
  );
};

export default ServerPage;
